/// <reference types="vite-plugin-svgr/client" />
import { ReactElement, useState, useCallback } from 'react';
import styles from './new-company-onboarding.module.scss';
import SafepointLogo from '@/common/assets/images/safepoint-logo-dark.svg?react';
import { UIProgressStepper, UISelectValue } from '@/ui';
import { OnboardingWelcome } from './onboarding-welcome';
import { OnboardingCompanyName } from './onboarding-company-name';
import { OnboardingPhoneNumber } from './onboarding-phone-number';
import { OnboardingTeam } from './onboarding-team';
import { useCompaniesTranslation } from '../hooks';
import { CreateCompanyRequest } from '@/api';

type NewCompanyOnboardingProps = {
    userEmail: string;
    loading: boolean;
    onSubmit: (data: CreateCompanyRequest) => void;
    onBack?: () => void;
};

export function NewCompanyOnboarding(
    props: NewCompanyOnboardingProps
): ReactElement {
    const { userEmail, loading, onSubmit, onBack } = props;

    const { t } = useCompaniesTranslation();

    const [activeStep, setActiveStep] = useState(0);

    const [companyName, setCompanyName] = useState('');
    const [phone, setPhone] = useState<{
        dialCode: string;
        number: string;
    } | null>(null);

    const [teamName, setTeamName] = useState('');
    const [users, setUsers] = useState<
        {
            email: string;
            admin: boolean;
            guardian: boolean;
            roles: UISelectValue[];
        }[]
    >([
        { email: '', admin: false, guardian: false, roles: [] },
        { email: '', admin: false, guardian: false, roles: [] },
        { email: '', admin: false, guardian: false, roles: [] }
    ]);

    const companyNameOnChange = useCallback((companyName: string) => {
        setCompanyName(companyName);
    }, []);

    const phoneOnChange = useCallback(
        (phone: { dialCode: string; number: string }) => {
            setPhone(phone);
        },
        []
    );

    const teamOnChange = useCallback(
        (data: {
            teamName: string;
            users: {
                email: string;
                admin: boolean;
                guardian: boolean;
                roles: UISelectValue[];
            }[];
        }) => {
            setTeamName(data.teamName);
            setUsers(data.users);
        },
        []
    );

    return (
        <div className={styles.container}>
            <div className={styles.innerContainer}>
                <div className={styles.headerContainer}>
                    <div className={styles.headerLogoContainer}>
                        <SafepointLogo width={200} />
                    </div>
                    <div className={styles.headerProgressStepperContainer}>
                        <UIProgressStepper
                            steps={[
                                t(
                                    'components.new_company_onboarding.welcome_stepper_title'
                                ),
                                t(
                                    'components.new_company_onboarding.company_name_stepper_title'
                                ),
                                t(
                                    'components.new_company_onboarding.phone_stepper_title'
                                ),
                                t(
                                    'components.new_company_onboarding.team_stepper_title'
                                )
                            ]}
                            activeStep={activeStep}
                            onStepClick={(step) => {
                                if (!loading) {
                                    setActiveStep(step);
                                }
                            }}
                        />
                    </div>
                </div>
                <div className={styles.bodyContainer}>
                    {activeStep === 0 && (
                        <OnboardingWelcome
                            onContinue={() => {
                                setActiveStep(1);
                            }}
                            onBack={onBack}
                        />
                    )}
                    {activeStep === 1 && (
                        <OnboardingCompanyName
                            initialCompanyName={companyName}
                            onChange={companyNameOnChange}
                            onContinue={() => {
                                setActiveStep(2);
                            }}
                            onBack={() => {
                                setActiveStep(0);
                            }}
                        />
                    )}
                    {activeStep === 2 && (
                        <OnboardingPhoneNumber
                            initialPhone={phone}
                            onChange={phoneOnChange}
                            onContinue={() => {
                                setActiveStep(3);
                            }}
                            onBack={() => {
                                setActiveStep(1);
                            }}
                        />
                    )}
                    {activeStep === 3 && (
                        <OnboardingTeam
                            userEmail={userEmail}
                            initialTeamName={teamName}
                            initialUsers={users}
                            loading={loading}
                            onChange={teamOnChange}
                            onContinue={() => {
                                const data = {
                                    companyName,
                                    teamName,
                                    phone: phone
                                        ? phone.dialCode + phone.number
                                        : undefined,
                                    users: users
                                        .filter((user) => user.email.length > 0)
                                        .map((user) => {
                                            const teamRoles: (
                                                | 'admin'
                                                | 'guardian'
                                            )[] = [];
                                            const companyRole:
                                                | 'owner'
                                                | 'admin'
                                                | 'billing'
                                                | 'member' = 'member';
                                            if (
                                                user.roles
                                                    .map((role) => role.value)
                                                    .includes('admin')
                                            ) {
                                                teamRoles.push('admin');
                                            }
                                            if (
                                                user.roles
                                                    .map((role) => role.value)
                                                    .includes('guardian')
                                            ) {
                                                teamRoles.push('guardian');
                                            }
                                            return {
                                                email: user.email,
                                                companyRole,
                                                teamRoles
                                            };
                                        })
                                };
                                onSubmit(data);
                            }}
                            onBack={() => {
                                setActiveStep(2);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
